import React, { useEffect, useState } from 'react';
import { learnerApi } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import Spinner from '../../components/common/Spinner';
import { FaFilePdf, FaDownload, FaSearch, FaUser, FaBook, FaFilter } from 'react-icons/fa';

const FILE_ICONS = {
  'application/pdf': { icon: '📄', color: '#ef4444', label: 'PDF' },
  'application/msword': { icon: '📝', color: '#0d6efd', label: 'Word' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: '📝', color: '#0d6efd', label: 'Word' },
  'application/vnd.ms-powerpoint': { icon: '📊', color: '#f59e0b', label: 'PPT' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { icon: '📊', color: '#f59e0b', label: 'PPT' },
};

export default function LearnerMaterials() {
  const { school }  = useAuth();
  const location    = useLocation();
  const primary     = school?.primary_color || '#0d6efd';

  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [subjectId,  setSubjectId]  = useState(location.state?.subjectId || '');
  const [search,     setSearch]     = useState('');
  const [view,       setView]       = useState('grid'); // 'grid' | 'list'

  useEffect(() => {
    setLoading(true);
    learnerApi.getMaterials(subjectId || null)
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subjectId]);

  const materials = data?.materials || [];
  const subjects  = data?.subjects  || [];

  const filtered = materials.filter(m =>
    !search ||
    m.title.toLowerCase().includes(search.toLowerCase()) ||
    m.subject_name.toLowerCase().includes(search.toLowerCase()) ||
    `${m.teacher_first_name} ${m.teacher_surname}`.toLowerCase().includes(search.toLowerCase())
  );

  // Group by subject
  const grouped = filtered.reduce((acc, m) => {
    const k = m.subject_name;
    if (!acc[k]) acc[k] = { subject_id: m.subject_id, materials: [] };
    acc[k].materials.push(m);
    return acc;
  }, {});

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h2>Study Materials</h2>
          <p>{data?.total || 0} resource{data?.total !== 1 ? 's' : ''} available</p>
        </div>
        {/* View toggle */}
        <div style={{ display:'flex', gap:4, background:'#f3f4f6', borderRadius:10, padding:4 }}>
          {[['grid','⊞ Grid'],['list','≡ List']].map(([k,l]) => (
            <button key={k} onClick={() => setView(k)} className="sms-btn"
              style={{ background:view===k?'#fff':'transparent', boxShadow:view===k?'0 1px 4px rgba(0,0,0,0.1)':'none',
                color:view===k?'#111':'#6b7280', padding:'6px 14px', borderRadius:8, fontSize:'0.82rem' }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:1, maxWidth:320 }}>
          <FaSearch style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#9ca3af' }} />
          <input className="sms-input" style={{ paddingLeft:36 }} placeholder="Search materials..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="sms-input" style={{ width:200 }} value={subjectId} onChange={e => setSubjectId(e.target.value)}>
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>)}
        </select>
      </div>

      {loading ? <Spinner /> : filtered.length === 0 ? (
        <div className="sms-card" style={{ textAlign:'center', color:'#9ca3af', padding:'60px 0' }}>
          <FaBook size={44} style={{ marginBottom:12, opacity:0.3 }} /><br />
          {search ? `No materials matching "${search}"` : 'No study materials available yet'}
        </div>
      ) : view === 'grid' ? (
        /* Grid view — grouped by subject */
        Object.entries(grouped).map(([subjectName, group]) => (
          <div key={subjectName} style={{ marginBottom:28 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
              <div style={{ width:8, height:24, borderRadius:4, background:primary }} />
              <h5 style={{ fontWeight:800, margin:0, color:'#374151' }}>{subjectName}</h5>
              <span style={{ fontSize:'0.75rem', color:'#9ca3af', background:'#f3f4f6', padding:'2px 8px', borderRadius:12 }}>
                {group.materials.length} file{group.materials.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:14 }}>
              {group.materials.map(m => {
                const fileInfo = FILE_ICONS[m.file_type] || { icon:'📎', color:'#6b7280', label:'File' };
                return (
                  <div key={m.id} className="sms-card-sm" style={{ display:'flex', flexDirection:'column', gap:10,
                    borderLeft:`3px solid ${fileInfo.color}`, transition:'box-shadow 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'}>

                    {/* File type badge + title */}
                    <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                      <div style={{ width:40, height:40, borderRadius:8, background:`${fileInfo.color}18`,
                        display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'1.2rem' }}>
                        {fileInfo.icon}
                      </div>
                      <div style={{ flex:1, overflow:'hidden' }}>
                        <div style={{ fontWeight:700, fontSize:'0.9rem', lineHeight:1.3 }}>{m.title}</div>
                        <span style={{ fontSize:'0.68rem', background:`${fileInfo.color}18`, color:fileInfo.color,
                          padding:'1px 6px', borderRadius:8, fontWeight:600 }}>{fileInfo.label}</span>
                      </div>
                    </div>

                    {/* Description */}
                    {m.description && (
                      <p style={{ color:'#4b5563', fontSize:'0.82rem', margin:0, lineHeight:1.5,
                        display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                        {m.description}
                      </p>
                    )}

                    {/* Teacher */}
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ width:28, height:28, borderRadius:'50%', background:`${primary}22`,
                        display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}>
                        {m.teacher_avatar
                          ? <img src={m.teacher_avatar} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                          : <FaUser size={12} color={primary} />
                        }
                      </div>
                      <div>
                        <div style={{ fontSize:'0.75rem', fontWeight:600, color:'#374151' }}>
                          {m.teacher_first_name} {m.teacher_surname}
                        </div>
                        <div style={{ fontSize:'0.68rem', color:'#9ca3af' }}>
                          {new Date(m.uploaded_at).toLocaleDateString('en', { day:'numeric', month:'short', year:'numeric' })}
                          {m.file_size_human && ` · ${m.file_size_human}`}
                        </div>
                      </div>
                    </div>

                    {/* Download */}
                    <a href={m.file_url} target="_blank" rel="noreferrer"
                      className="sms-btn sms-btn-primary sms-btn-sm"
                      style={{ justifyContent:'center', background:fileInfo.color, marginTop:'auto' }}>
                      <FaDownload size={11} /> Download
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      ) : (
        /* List view */
        <div className="sms-card" style={{ padding:0, overflow:'hidden' }}>
          <table className="sms-table">
            <thead>
              <tr><th>Type</th><th>Title</th><th>Subject</th><th>Uploaded by</th><th>Date</th><th>Size</th><th>Download</th></tr>
            </thead>
            <tbody>
              {filtered.map(m => {
                const fi = FILE_ICONS[m.file_type] || { icon:'📎', color:'#6b7280', label:'File' };
                return (
                  <tr key={m.id}>
                    <td>
                      <span style={{ fontSize:'1.1rem' }}>{fi.icon}</span>
                      <span style={{ marginLeft:4, fontSize:'0.72rem', color:fi.color, fontWeight:600 }}>{fi.label}</span>
                    </td>
                    <td>
                      <div style={{ fontWeight:600, fontSize:'0.88rem' }}>{m.title}</div>
                      {m.description && <div style={{ color:'#9ca3af', fontSize:'0.75rem', marginTop:1 }}>{m.description.slice(0,60)}{m.description.length>60?'…':''}</div>}
                    </td>
                    <td style={{ fontWeight:500 }}>{m.subject_name}</td>
                    <td style={{ fontSize:'0.83rem', color:'#6b7280' }}>{m.teacher_first_name} {m.teacher_surname}</td>
                    <td style={{ fontSize:'0.8rem', color:'#6b7280', whiteSpace:'nowrap' }}>
                      {new Date(m.uploaded_at).toLocaleDateString()}
                    </td>
                    <td style={{ fontSize:'0.8rem', color:'#9ca3af' }}>{m.file_size_human || '—'}</td>
                    <td>
                      <a href={m.file_url} target="_blank" rel="noreferrer"
                        className="sms-btn sms-btn-outline sms-btn-sm" style={{ color:fi.color, borderColor:`${fi.color}44` }}>
                        <FaDownload size={11} />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
